import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AnalogInput } from 'z3k-config';
import { MaterialTableWithDnd } from 'console/table/drag-drop/drag-drop-table';
import memoizeOne from 'memoize-one';
import * as lodash from 'lodash';
import { parseFloatWithComma, prettyFloat } from 'webui/utils/number';
import { StrictNumberInput } from 'console/shared/components/fields';
import { ControlWithoutLabel } from 'console/shared/components/fields/control/without-label';
import { minMax, notNullAnd } from 'z3k-parser/validation/basic';
import { Alert, Box, InputAdornment, TableCell } from '@mui/material';
const convertToUserView = memoizeOne((table_items, multipliers) => {
    return table_items.map(({ value, voltage }) => ({
        value: parseFloat(prettyFloat(value / multipliers.value, 6)),
        voltage: parseFloat(prettyFloat(voltage / multipliers.voltage, 6)),
    }));
});
const convertFromUserView = memoizeOne((table_items, multipliers) => {
    return table_items.map(({ value, voltage }) => {
        const n_value = parseFloatWithComma(value);
        const n_voltage = parseFloatWithComma(voltage);
        if (n_value == null || n_voltage == null) {
            throw new Error('invalid table');
        }
        return {
            value: n_value * multipliers.value,
            voltage: n_voltage * multipliers.voltage,
        };
    });
});
export function AnalogInputCalibrationTable(props) {
    const { analog_input, value, onChange, validation_result, validation_warnings_result, multiply_x, } = props;
    const multipliers = useMemo(() => ({
        value: AnalogInput.getMultiplier(analog_input),
        voltage: multiply_x,
    }), [analog_input, multiply_x]);
    const captions = useMemo(() => ({
        value: AnalogInput.getUnit(analog_input),
        voltage: AnalogInput.isUseResistance(analog_input)
            ? 'кОм'
            : AnalogInput.isAmperInputType(analog_input) // Проверка проходит по названию выхода (плохо)
                ? 'мА'
                : 'В',
    }), [analog_input]);
    const [temp_items, setTempItems] = useState(convertToUserView(value, multipliers));
    useEffect(() => {
        const new_temps_obj = convertToUserView(value, multipliers);
        if (!lodash.isEqual(new_temps_obj, setTempItems)) {
            setTempItems(new_temps_obj);
        }
    }, [value]);
    const onChangeTableData = useCallback((new_data) => {
        setTempItems(new_data);
        onChange(convertFromUserView(new_data, multipliers));
    }, [setTempItems, multipliers]);
    const onClickAddButton = useCallback(() => onChangeTableData([...temp_items, { value: 0, voltage: 0 }]), [onChangeTableData, temp_items]);
    const AnalogInputCalibrationRow = useCallback((props) => {
        const { values, onChangeRow } = props;
        const { value, voltage } = values;
        const onChangeValue = useCallback((new_value) => {
            onChangeRow({ voltage, value: new_value });
        }, [onChangeRow, voltage]);
        const onChangeVoltage = useCallback((new_value) => {
            onChangeRow({ voltage: new_value, value });
        }, [onChangeRow, value]);
        const validation_result = analog_input.units === AnalogInput.Units.VOLUME
            ? notNullAnd(minMax(-1000, 10000, 'Недопустимое значение'))(value)
            : notNullAnd(minMax(-1000, 1000, 'Недопустимое значение'))(value);
        return (_jsxs(_Fragment, { children: [_jsx(TableCell, { children: _jsx(ControlWithoutLabel, { onChange: onChangeValue, value: value, field: (input_props) => (_jsx(StrictNumberInput, { ...input_props, min: -1000, step: 1 / multipliers.value, max: 1000, endAdornment: _jsx(InputAdornment, { position: "end", children: captions.value }) })), validation_result: validation_result, validation_warnings_result: null }) }), _jsx(TableCell, { children: _jsx(ControlWithoutLabel, { onChange: onChangeVoltage, value: voltage, field: (input_props) => (_jsx(StrictNumberInput, { ...input_props, min: -1000, step: 1 / multipliers.voltage, max: 1000, endAdornment: _jsx(InputAdornment, { position: "end", children: captions.voltage }) })), validation_result: notNullAnd(minMax(-1000, 1000, 'Недопустимое значение'))(voltage), validation_warnings_result: null }) })] }));
    }, [captions, multipliers]);
    const is_validation_failed = useMemo(() => validation_result != null || validation_warnings_result != null, [validation_result, validation_warnings_result]);
    const has_warning = useMemo(() => validation_result == null && validation_warnings_result != null, [validation_result, validation_warnings_result]);
    const has_error = useMemo(() => validation_result != null, [validation_result]);
    return (_jsxs(Box, { display: "flex", flexDirection: "column", gap: 1, width: "100%", children: [_jsx(MaterialTableWithDnd, { tableHeaderProps: {
                    columns_name: [
                        'Пересчитанное значение',
                        AnalogInput.isUseResistance(analog_input)
                            ? 'Сопротивление'
                            : AnalogInput.isAmperInputType(analog_input)
                                ? 'Сила тока'
                                : 'Напряжение',
                    ],
                    withRemoveButton: true,
                    withAddButton: true,
                    addButtonClick: onClickAddButton,
                }, containerProps: {
                    style: {
                        overflowX: 'hidden',
                    },
                }, tableProps: {
                    size: 'small',
                }, table_data: temp_items, onChangeData: onChangeTableData, rowComponentFunction: AnalogInputCalibrationRow }), is_validation_failed && (_jsx(Box, { my: 1, children: _jsx(Alert, { severity: has_error ? 'error' : has_warning ? 'warning' : 'success', children: validation_result ??
                        validation_warnings_result }) }))] }));
}
